import { Inject, Injectable } from '@angular/core';
import { mapFetchResultData } from '@x/common/graph';
import { Observable } from 'rxjs';
import {
  ShopExportOrderInvoiceGQL,
  ShopOrderCreateGQL,
  ShopOrderCreateTokenGQL,
  ShopOrderDeleteItemGQL,
  ShopOrderGQL,
  ShopOrderRefreshTokenGQL,
  ShopOrderStateGQL,
  ShopOrderUpdateAddressGQL,
  ShopOrderUpdateCollectionPointGQL,
  ShopOrderUpdateCustomerGQL,
  ShopOrderUpdateItemGQL,
  ShopOrderUpdateItemsGQL,
  ShopOrderUpdateReferrerGQL,
} from './gql/shop-order.gql.generated';
import { AUTHORIZATION_HEADER_PROVIDER, AuthorizationHeaderProvider } from './types/shop-auth';
import {
  IShopCreateCart,
  IShopExportOrderInvoice,
  IShopExportOrderInvoiceMutation,
  IShopOrder,
  IShopOrderCreateMutation,
  IShopOrderCreateTokenMutation,
  IShopOrderDeleteItemMutation,
  IShopOrderId,
  IShopOrderRefreshTokenMutation,
  IShopOrderState,
  IShopOrderStateQuery,
  IShopOrderToken,
  IShopOrderUpdateAddressMutation,
  IShopOrderUpdateCollectionPointMutation,
  IShopOrderUpdateCustomerMutation,
  IShopOrderUpdateItemMutation,
  IShopOrderUpdateItemsMutation,
  IShopOrderUpdateReferrerMutation,
} from './types/shop-order';

@Injectable()
export class ShopOrderService {
  constructor(
    @Inject(AUTHORIZATION_HEADER_PROVIDER)
    private authHeader: AuthorizationHeaderProvider,
    private shopOrderCreateGQL: ShopOrderCreateGQL,
    private shopOrderUpdateItemGQL: ShopOrderUpdateItemGQL,
    private shopOrderUpdateItemsGQL: ShopOrderUpdateItemsGQL,
    private shopOrderDeleteItemGQL: ShopOrderDeleteItemGQL,
    private shopOrderUpdateAddressGQL: ShopOrderUpdateAddressGQL,
    private shopOrderUpdateCustomerGQL: ShopOrderUpdateCustomerGQL,
    private shopOrderGQL: ShopOrderGQL,
    private shopOrderUpdateCollectionPointGQL: ShopOrderUpdateCollectionPointGQL,
    private shopOrderCreateTokenGQL: ShopOrderCreateTokenGQL,
    private shopOrderRefreshTokenGQL: ShopOrderRefreshTokenGQL,
    private shopOrderStateGQL: ShopOrderStateGQL,
    private shopOrderUpdateReferrerGQL: ShopOrderUpdateReferrerGQL,
    private shopExportOrderInvoiceGQL: ShopExportOrderInvoiceGQL,
  ) {
    if (!authHeader) throw new Error('authHeader Provider is required');
  }

  createCart(query: IShopOrderCreateMutation): Observable<IShopCreateCart> {
    return this.shopOrderCreateGQL.mutate(query).pipe(mapFetchResultData((r) => r.shopCreateCart));
  }

  updateCartItem(query: IShopOrderUpdateItemMutation): Observable<IShopOrder> {
    return this.shopOrderUpdateItemGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartItem));
  }

  updateCartItems(query: IShopOrderUpdateItemsMutation): Observable<IShopOrder> {
    return this.shopOrderUpdateItemsGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartItems));
  }

  deleteCartItem(query: IShopOrderDeleteItemMutation): Observable<IShopOrder> {
    return this.shopOrderDeleteItemGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopDeleteCartItem));
  }

  updateCartAddress(query: IShopOrderUpdateAddressMutation): Observable<IShopOrder> {
    return this.shopOrderUpdateAddressGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartAddress));
  }

  updateCartCustomer(query: IShopOrderUpdateCustomerMutation): Observable<IShopOrder> {
    return this.shopOrderUpdateCustomerGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartCustomer));
  }

  fetchCart(id: IShopOrderId): Observable<IShopOrder> {
    return this.shopOrderGQL
      .fetch({ id: id }, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder));
  }

  updateCartCollectionPoint(
    query: IShopOrderUpdateCollectionPointMutation,
  ): Observable<IShopOrder> {
    return this.shopOrderUpdateCollectionPointGQL
      .mutate(query, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartCollectionPoint));
  }

  createOrderToken(input: IShopOrderCreateTokenMutation): Observable<IShopOrderToken> {
    return this.shopOrderCreateTokenGQL
      .mutate(input, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopCreateCartToken));
  }

  refreshOrderToken(input?: IShopOrderRefreshTokenMutation): Observable<IShopOrderToken> {
    return this.shopOrderRefreshTokenGQL
      .mutate(input, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopRefreshCartToken));
  }

  fetchOrderState(args: IShopOrderStateQuery): Observable<IShopOrderState> {
    return this.shopOrderStateGQL
      .fetch(args, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopOrder.state));
  }

  updateReferrer(args: IShopOrderUpdateReferrerMutation): Observable<IShopOrder> {
    return this.shopOrderUpdateReferrerGQL
      .mutate(args, this.authHeader.orderAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopUpdateCartReferrer));
  }

  exportOrderInvoice(args: IShopExportOrderInvoiceMutation): Observable<IShopExportOrderInvoice> {
    return this.shopExportOrderInvoiceGQL
      .mutate(args, this.authHeader.userAuthHeader)
      .pipe(mapFetchResultData((r) => r.shopExportOrderInvoice));
  }
}
